
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { useProfile } from "@/hooks/useProfile";

interface QuixUploadDialogProps {
  onUploadComplete?: () => void;
}

export const QuixUploadDialog = ({ onUploadComplete }: QuixUploadDialogProps) => {
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { profile } = useProfile();

  const handleProCheck = () => {
    if (!profile?.is_pro) {
      toast({
        title: "Pro Membership Required",
        description: "Sorry, video uploading is only available for Pro members.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.is_pro) return;
    if (!videoFile) {
      toast({
        title: "No video selected",
        description: "Please select a video to upload",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Upload video to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("quix-videos")
        .upload(`${Date.now()}-${videoFile.name}`, videoFile);

      if (uploadError) throw uploadError;

      // Create video entry in database
      const { error: dbError } = await supabase
        .from("quix_videos")
        .insert({
          url: uploadData.path,
          description,
          user_id: profile.id,
        });

      if (dbError) throw dbError;

      toast({
        title: "Success!",
        description: "Your video has been uploaded.",
      });

      // Reset form
      setVideoFile(null);
      setDescription("");
      
      // Call the onUploadComplete callback if provided
      onUploadComplete?.();
    } catch (error) {
      console.error("Error uploading video:", error);
      toast({
        title: "Upload failed",
        description: "There was an error uploading your video. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full bg-black/50 hover:bg-black/70 transition-all duration-300"
          onClick={(e) => {
            if (!handleProCheck()) {
              e.preventDefault();
            }
          }}
        >
          <Upload className="w-6 h-6 text-white" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Upload Video</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="video">Video</Label>
            <Input
              id="video"
              type="file"
              accept="video/*"
              onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Write a description for your video..."
              required
            />
          </div>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Uploading..." : "Upload Video"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
